import { useQueryClient } from "@tanstack/react-query";
import { Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CustomDateTabs, CustomTable, Filter, FilterTag } from "../../../../../components";
import { moneyFormat } from "../../../../../plugins/numberFormat";
import { getCurrency } from "../../../../../plugins/utils";
import { filterKeyAgentData } from "../../enumerations/filterKeyEnum";
import { filterSetupAgentData } from "../../enumerations/filterSetupEnum";
import { useGetSubordinateAgentData } from "../../hooks/subordinate.hooks";
import styles from "./style.module.scss";
const AgentData = ({ outerTab, setOuterTab, setCurrentUser, setCurrentSearchTime }) => {
  const nowCurrency = getCurrency();
  const { t } = useTranslation();
  const cache = useQueryClient();

  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyAgentData.ACCOUNT]: "",
    [filterKeyAgentData.ACCOUNT_ID]: "",
    [filterKeyAgentData.AGENT_ACCOUNT]: "",
    [filterKeyAgentData.INVITE_CODE]: "",
    [filterKeyAgentData.START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyAgentData.END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });
  const [filterValue, setFilterValue] = useState({
    [filterKeyAgentData.ACCOUNT]: "",
    [filterKeyAgentData.ACCOUNT_ID]: "",
    [filterKeyAgentData.AGENT_ACCOUNT]: "",
    [filterKeyAgentData.INVITE_CODE]: "",
    [filterKeyAgentData.START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyAgentData.END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });

  const [localData, setLocalData] = useState([]);
  const [summary, setSummary] = useState({});

  const agentDataColumns = [
    {
      title: t("mobile.subordinate.agentAccount"),
      width: 230,
      dataIndex: "account",
      key: "account",
      align: "center",
      render: (text, record, index) => {
        return (
          <span
            style={{ color: "var(--adm-color-primary)", cursor: "pointer" }}
            onClick={() => {
              setOuterTab("member_data");
              setCurrentUser(text);
              setCurrentSearchTime({
                startTime: filterValue[filterKeyAgentData.START_DATE],
                endTime: filterValue[filterKeyAgentData.END_DATE],
              });
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: t("normal.level"),
      width: 130,
      dataIndex: "level",
      key: "level",
      align: "center",
      // render: (text, record, index) => {
      //   return <>1</>;
      // },
    },
    {
      title: t("normal.uid"),
      width: 90,
      dataIndex: "account_id",
      key: "account_id",
      align: "center",
    },
    {
      title: t("mobile.subordinate.inviteCode"),
      width: 90,
      dataIndex: "invite_code",
      key: "invite_code",
      align: "center",
    },
    {
      title: t("agentData.reviewTime"),
      width: 150,
      dataIndex: "agent_reviewed_at",
      key: "agent_reviewed_at",
      align: "center",
    },
    {
      title: t("normal.sub.totalMemberCount"),
      width: 100,
      dataIndex: "child_count",
      key: "child_count",
      align: "center",
    },
    {
      title: t("agentData.newRegister"),
      width: 100,
      dataIndex: "child_register_count",
      key: "child_register_count",
      align: "center",
    },
    {
      title: t("agentData.depositMemberNum"),
      width: 100,
      dataIndex: "sum_deposit_count",
      key: "sum_deposit_count",
      align: "center",
    },
    {
      title: t("agentData.totalDepositAmount", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_deposit_amount",
      key: "sum_deposit_amount",
      width: 120,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("agentData.withdrawMemberNum"),
      width: 90,
      dataIndex: "sum_withdrawal_count",
      key: "sum_withdrawal_count",
      align: "center",
    },
    {
      title: t("agentData.totalWithdrawAmount", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_withdrawal_amount",
      key: "sum_withdrawal_amount",
      width: 120,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("agentData.betMemberNum"),
      width: 90,
      dataIndex: "bet_user_count",
      key: "bet_user_count",
      align: "center",
    },
    {
      title: t("agentData.subordinateEffectiveBet", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_bet_amount",
      key: "sum_bet_amount",
      width: 150,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("agentData.bonus", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_promotion_rebate_amount",
      key: "sum_promotion_rebate_amount",
      width: 120,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("agentData.subordinateWinLose", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_win_loss",
      key: "sum_win_loss",
      width: 130,
      fixed: "right",
      align: "center",
      render: (text, record, index) => {
        return (
          <div className={`${parseFloat(text) > 0 ? styles.textGreen : styles.textRed}`}>
            {moneyFormat(text)}
          </div>
        );
      },
    },
  ];

  const {
    data: userData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
  } = useGetSubordinateAgentData({
    params: {
      ...filterValue,
    },
    options: {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });

  const loadMore = () => {
    if (isFetchingNextPage === false && localData.length > 0) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (userData) {
      const page_length = userData.pages.length;
      if (page_length <= 1) {
        setLocalData([...userData.pages[0].data]);
        setSummary({ ...userData.pages[0] });
      } else {
        setLocalData((prevState) => {
          return [...prevState, ...userData.pages[page_length - 1].data];
        });
        setSummary({ ...userData.pages[page_length - 1] });
      }
    }
  }, [userData]);

  const onHandleSearch = () => {};

  return (
    <div>
      <Filter
        filterSetup={filterSetupAgentData}
        filterDefaultValue={filterDefaultValue}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
        }}
        onSubmitSearch={onHandleSearch}
        activeTab={outerTab}
      />
      <CustomDateTabs
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        startDateKey={filterKeyAgentData.START_DATE}
        endDateKey={filterKeyAgentData.END_DATE}
      />
      <FilterTag
        filterSetup={filterSetupAgentData}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        refreshSearch={onHandleSearch}
      />
      {isLoading ? (
        ""
      ) : isError ? (
        ""
      ) : (
        <CustomTable
          columns={agentDataColumns}
          dataSource={localData}
          hasMore={hasNextPage}
          loadMore={loadMore}
          showInifineScroll={true}
          summary={() => {
            return (
              <Table.Summary fixed="top">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <div className={styles.summaryCell}>{t("normal.summary")}</div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_child_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_child_register_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_deposit_user_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_sum_deposit_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_withdrawal_user_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_sum_withdrawal_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {summary?.total_bet_user_count || 0}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_sum_bet_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={13}>
                    <div className={`${styles.summaryCell} ${styles.content}`}>
                      {moneyFormat(summary?.total_sum_promotion_rebate_amount || 0)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={14}>
                    <div
                      className={`${styles.summaryCell} ${styles.content} ${
                        !summary?.total_sum_win_loss || summary?.total_sum_win_loss <= 0
                          ? styles.redColor
                          : styles.greenColor
                      }`}
                    >
                      {moneyFormat(summary?.total_sum_win_loss || 0)}
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
    </div>
  );
};

export default AgentData;
